import type { identifier } from '@core/models/base';
import { authedAxios, getAuthorizationHeader } from '@shared/axios';
import { getAdminTabSessionId } from '@shared/adminSessionTabId';

export interface HolderActivity {
  holderIsWriting: boolean;
  /** Null when the holder has never made a mutating request (or there is no lease). */
  secondsSinceLastWrite: number | null;
  quietWindowSeconds: number;
}

/**
 * Endpoints for the single-admin-session lease (singleAdminSession feature flag).
 * Raw authedAxios rather than axiosToServiceResult: callers need rejections to
 * distinguish a failed claim, and the 409 conflict is handled globally in the
 * authedAxios response interceptor.
 */
export const adminSessionService = {
  /**
   * The claim attempt on entering the admin area: the guard grants the seat when it's
   * free or already this tab's, and 409s when another session holds it.
   */
  heartbeat(studioId: identifier) {
    return authedAxios.post(`/api/studioadmin/studios/${studioId}/admin-session/heartbeat`);
  },

  /**
   * Moves the seat to this tab — unless the holder wrote within the server's quiet
   * window, in which case it 409s with code adminSessionHolderActive (the store's
   * pre-click activity check is advisory; the server is the enforcement). Only ever
   * called from the conflict modal's take-over button — the question flow before it
   * is the deliberateness gate.
   */
  takeover(studioId: identifier) {
    return authedAxios.post(`/api/studioadmin/studios/${studioId}/admin-session/takeover`);
  },

  /**
   * Whether the seat holder made a mutating request within the server's quiet window —
   * takeover is refused while true (an actively-working session is protected from being
   * bumped). The raw age and window size ride along so the conflict modal can show a
   * live "still active / gone quiet / you can continue" line from the same numbers.
   */
  async holderActivity(studioId: identifier): Promise<HolderActivity> {
    const response = await authedAxios.get<HolderActivity>(
      `/api/studioadmin/studios/${studioId}/admin-session/holder-activity`
    );
    return response.data;
  },

  /**
   * Best-effort checkout as the tab closes. fetch keepalive (not sendBeacon — it can't set
   * headers) outlives the page; if it fails, the next tab simply sees the conflict modal
   * and takes over — the seat never frees itself.
   */
  releaseOnUnload(studioId: identifier) {
    const authorization = getAuthorizationHeader();
    if (!authorization) return;

    fetch(`/api/studioadmin/studios/${studioId}/admin-session/release`, {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: authorization,
        'X-Admin-Session-Id': getAdminTabSessionId(),
      },
    }).catch(() => {
      /* best-effort */
    });
  },
};
